import React, { useState } from 'react';
import { router, Link } from '@inertiajs/react';
import { AlertTriangle, ShieldAlert, ShieldCheck } from 'lucide-react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  DataTable,
  Badge,
  Button,
  FilterBar,
  StatCard,
  Tabs,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Checkbox,
} from '../../../design-system/components';
import { formatDateTime } from '../../../lib/formatters';
import {
  admin_fraud_root_path,
  admin_affiliate_path,
  confirm_admin_fraud_event_path,
  clear_admin_fraud_event_path,
  admin_fraud_rules_path,
  admin_fraud_disposable_emails_path,
  admin_fraud_reserved_words_path,
} from '../../../lib/routes';
const RULE_TYPES = [
  'self_referral',
  'paid_traffic',
  'brand_bidding',
  'velocity_spike',
  'disposable_email',
  'duplicate_payment_method',
  'ip_match',
  'custom',
];
const statusVariant = {
  flagged: 'warning',
  confirmed_fraud: 'danger',
  cleared: 'success',
};
const ruleTypeLabel = {
  self_referral: 'Self-Referral',
  paid_traffic: 'Paid Traffic',
  brand_bidding: 'Brand Bidding',
  velocity_spike: 'Velocity Spike',
  disposable_email: 'Disposable Email',
  duplicate_payment_method: 'Duplicate Payment',
  ip_match: 'IP Match',
  custom: 'Custom Rule',
};
function severityColor(ruleType) {
  switch (ruleType) {
    case 'self_referral':
    case 'duplicate_payment_method':
      return 'text-danger-600';
    case 'velocity_spike':
    case 'brand_bidding':
      return 'text-warning-600';
    case 'paid_traffic':
    case 'ip_match':
      return 'text-warning-500';
    default:
      return 'text-fg-secondary';
  }
}
export default function Index({ events, tab, affiliates, filters, pagination, stats }) {
  const [confirmingId, setConfirmingId] = useState(null);
  const [clearingId, setClearingId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deactivateAffiliate, setDeactivateAffiliate] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  function toggleExpanded(id) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }
  function handleConfirm() {
    if (confirmingId === null) return;
    setActionLoading(true);
    router.post(
      confirm_admin_fraud_event_path(confirmingId),
      {
        deactivate_affiliate: deactivateAffiliate ? '1' : '0',
      },
      {
        preserveScroll: true,
        onFinish: () => {
          setActionLoading(false);
          setShowConfirmDialog(false);
          setConfirmingId(null);
          setDeactivateAffiliate(false);
        },
      },
    );
  }
  function handleClear(id) {
    setClearingId(id);
    router.post(
      clear_admin_fraud_event_path(id),
      {},
      {
        preserveScroll: true,
        onFinish: () => setClearingId(null),
      },
    );
  }
  function handleTabChange(newTab) {
    router.visit(admin_fraud_root_path(), {
      data: {
        tab: newTab,
      },
      preserveState: false,
    });
  }
  function handlePageChange(page) {
    router.visit(admin_fraud_root_path(), {
      data: {
        tab,
        ...filters,
        page: String(page),
      },
      preserveState: true,
      preserveScroll: true,
      replace: true,
    });
  }
  const queueColumns = [
    {
      id: 'affiliate_name',
      header: 'Affiliate',
      render: (row) => (
        <Link href={admin_affiliate_path(row.affiliate_id)} className="font-medium text-fg hover:text-fg-secondary">
          {row.affiliate_name}
        </Link>
      ),
    },
    {
      id: 'rule_type',
      header: 'Rule',
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${severityColor(row.rule_type)}`}>
            {ruleTypeLabel[row.rule_type] || row.rule_type}
          </span>
          {row.fraud_rule_name &&
            row.fraud_rule_name !== (ruleTypeLabel[row.rule_type] || row.rule_type) && (
              <span className="text-xs text-fg-tertiary">({row.fraud_rule_name})</span>
            )}
        </div>
      ),
    },
    {
      id: 'details',
      header: 'Details',
      render: (row) => {
        const isExpanded = expandedRows.has(row.id);
        const details = typeof row.details === 'string' ? JSON.parse(row.details) : row.details;
        const entries = Object.entries(details);
        if (entries.length === 0) {
          return <span className="text-sm text-fg-tertiary">—</span>;
        }
        const preview = entries.slice(0, 2);
        return (
          <div className="text-sm">
            <div className="space-y-0.5">
              {(isExpanded ? entries : preview).map(([key, value]) => (
                <div key={key} className="text-fg-secondary">
                  <span className="font-medium text-fg-secondary">{key}:</span> {String(value)}
                </div>
              ))}
            </div>
            {entries.length > 2 && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-1 !px-0 !py-0 text-xs"
                onClick={() => toggleExpanded(row.id)}
              >
                {isExpanded ? 'Show less' : `+${entries.length - 2} more`}
              </Button>
            )}
          </div>
        );
      },
    },
    {
      id: 'referral_id',
      header: 'Referral',
      render: (row) => (
        <span className="text-sm text-fg-tertiary">
          {row.referral_id ? `#${row.referral_id}` : '—'}
        </span>
      ),
    },
    {
      id: 'created_at',
      header: 'Date',
      render: (row) => (
        <span className="text-sm text-fg-tertiary">{formatDateTime(row.created_at)}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={statusVariant[row.status] ?? 'default'} size="sm">
          {row.status.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="danger"
            loading={confirmingId === row.id && actionLoading}
            disabled={actionLoading}
            onClick={() => {
              setConfirmingId(row.id);
              setShowConfirmDialog(true);
            }}
          >
            Confirm
          </Button>
          <Button
            size="sm"
            variant="outline"
            loading={clearingId === row.id}
            disabled={clearingId !== null}
            onClick={() => handleClear(row.id)}
          >
            Clear
          </Button>
        </div>
      ),
    },
  ];
  const historyColumns = [
    {
      id: 'affiliate_name',
      header: 'Affiliate',
      render: (row) => (
        <Link href={admin_affiliate_path(row.affiliate_id)} className="font-medium text-fg hover:text-fg-secondary">
          {row.affiliate_name}
        </Link>
      ),
    },
    {
      id: 'rule_type',
      header: 'Rule',
      render: (row) => (
        <span className={`text-sm font-medium ${severityColor(row.rule_type)}`}>
          {ruleTypeLabel[row.rule_type] || row.rule_type}
        </span>
      ),
    },
    {
      id: 'details',
      header: 'Details',
      render: (row) => {
        const isExpanded = expandedRows.has(row.id);
        const details = typeof row.details === 'string' ? JSON.parse(row.details) : row.details;
        const entries = Object.entries(details);
        if (entries.length === 0) {
          return <span className="text-sm text-fg-tertiary">—</span>;
        }
        const preview = entries.slice(0, 2);
        return (
          <div className="text-sm">
            <div className="space-y-0.5">
              {(isExpanded ? entries : preview).map(([key, value]) => (
                <div key={key} className="text-fg-secondary">
                  <span className="font-medium text-fg-secondary">{key}:</span> {String(value)}
                </div>
              ))}
            </div>
            {entries.length > 2 && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-1 !px-0 !py-0 text-xs"
                onClick={() => toggleExpanded(row.id)}
              >
                {isExpanded ? 'Show less' : `+${entries.length - 2} more`}
              </Button>
            )}
          </div>
        );
      },
    },
    {
      id: 'referral_id',
      header: 'Referral',
      render: (row) => (
        <span className="text-sm text-fg-tertiary">
          {row.referral_id ? `#${row.referral_id}` : '—'}
        </span>
      ),
    },
    {
      id: 'created_at',
      header: 'Date',
      render: (row) => (
        <span className="text-sm text-fg-tertiary">{formatDateTime(row.created_at)}</span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={statusVariant[row.status] ?? 'default'} size="sm">
          {row.status.replace('_', ' ')}
        </Badge>
      ),
    },
  ];
  const columns = tab === 'queue' ? queueColumns : historyColumns;
  const filterDefs = [
    {
      param: 'affiliate_id',
      label: 'Affiliate',
      value: filters.affiliate_id || '',
      options: affiliates.map((a) => ({
        label: a.name,
        value: String(a.id),
      })),
    },
    {
      param: 'rule_type',
      label: 'Rule Type',
      value: filters.rule_type || '',
      options: RULE_TYPES.map((rt) => ({
        label: ruleTypeLabel[rt] || rt,
        value: rt,
      })),
    },
  ];
  const tabItems = [
    {
      id: 'queue',
      label: `Flagged (${stats.flagged_count})`,
    },
    {
      id: 'history',
      label: 'History',
    },
  ];
  return (
    <AdminLayout>
      <PageHeader
        title="Fraud Detection"
        description="Monitor and manage suspected fraud events"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.visit(admin_fraud_rules_path())}
            >
              Rules
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.visit(admin_fraud_disposable_emails_path())}
            >
              Disposable Emails
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.visit(admin_fraud_reserved_words_path())}
            >
              Reserved Words
            </Button>
          </div>
        }
      />

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Flagged"
          value={stats.flagged_count}
          icon={<AlertTriangle size={20} className="text-warning-500" />}
        />
        <StatCard
          label="Confirmed"
          value={stats.confirmed_count}
          icon={<ShieldAlert size={20} className="text-danger-500" />}
        />
        <StatCard
          label="Cleared"
          value={stats.cleared_count}
          icon={<ShieldCheck size={20} className="text-success-500" />}
        />
      </div>

      <div className="mt-6">
        <Tabs tabs={tabItems} activeTab={tab} onChange={handleTabChange} />
      </div>

      <div className="mt-4 space-y-4">
        <FilterBar url={admin_fraud_root_path()} filters={filterDefs} />

        <DataTable
          columns={columns}
          data={events}
          keyAccessor="id"
          pagination={{
            page: pagination.current_page,
            totalPages: pagination.total_pages,
            onPageChange: handlePageChange,
          }}
          emptyTitle={tab === 'queue' ? 'No flagged events' : 'No fraud event history'}
          emptyDescription={
            tab === 'queue'
              ? 'No suspected fraud events require review right now.'
              : 'Reviewed fraud events will appear here.'
          }
        />
      </div>

      <Modal
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setConfirmingId(null);
          setDeactivateAffiliate(false);
        }}
        size="sm"
      >
        <ModalTitle>
          <span className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-danger-500" aria-hidden="true" />
            Confirm Fraud
          </span>
        </ModalTitle>
        <ModalBody>
          <p className="text-sm text-fg-secondary">
            This will mark the event as confirmed fraud and reverse any associated commissions.
          </p>
          <div className="mt-4">
            <Checkbox
              label="Also deactivate this affiliate"
              description="Deactivate the affiliate account, revoke their promo code, and freeze pending commissions"
              checked={deactivateAffiliate}
              onChange={(e) => setDeactivateAffiliate(e.target.checked)}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setShowConfirmDialog(false);
              setConfirmingId(null);
              setDeactivateAffiliate(false);
            }}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleConfirm} loading={actionLoading}>
            Confirm Fraud
          </Button>
        </ModalFooter>
      </Modal>
    </AdminLayout>
  );
}
